// Aiguillage du chat : abonnement du studio ou API Anthropic.
//
// ⚠ Anthropic n'autorise pas un editeur tiers a faire passer les requetes de
// ses utilisateurs par des identifiants de formule Free, Pro ou Max. Seuls
// les comptes de Mendar et de Dylan passent par pixel-api (leurs propres
// abonnements) ; tous les autres passent par l'API, via l'edge function.
//
// Ce fichier n'est PAS un garde-fou de securite : la vraie barriere est le
// 403 rendu par pixel-api apres verification du JWT cote serveur. Ici on
// evite seulement un aller-retour inutile aux comptes non concernes.
//
// Ce qui a mis le chat en panne en aout 2026 : l'aiguillage etait fige AU
// MOMENT DU BUILD (`VITE_PIXEL_API_URL ? pixel-api : edge function`), donc
// TOUS les utilisateurs partaient vers un MacBook qui ne repondait plus,
// sans aucun repli. La decision se prend desormais par COMPTE, a l'execution,
// et l'echec retombe sur l'API.

const PIXEL_API_URL = (import.meta.env.VITE_PIXEL_API_URL as string | undefined)?.trim() || '';

// Comme cote serveur : '' = personne, 'a@b,c@d' = ces comptes, '*' = tous
// les comptes CONNECTES. Le joker ne concerne jamais un visiteur anonyme,
// qui n'a pas d'adresse et repart donc sur l'API (voir `passeParAbonnement`).
const LISTE_BRUTE = ((import.meta.env.VITE_PIXEL_API_EMAILS as string | undefined) || '').trim();
const TOUS_LES_COMPTES = LISTE_BRUTE === '*';
const COMPTES_ABONNEMENT = new Set(
  TOUS_LES_COMPTES
    ? []
    : LISTE_BRUTE.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
);

/** Delai au-dela duquel on considere pixel-api absent et on part sur l'API. */
const DELAI_CONNEXION_MS = 8000;

export function passeParAbonnement(email?: string | null): boolean {
  if (!PIXEL_API_URL || !email) return false;
  if (TOUS_LES_COMPTES) return true;
  return COMPTES_ABONNEMENT.has(email.toLowerCase());
}

/**
 * Renvoie le `fetch` que `useChat` doit utiliser.
 *
 * Compte eligible : pixel-api d'abord, repli automatique sur l'API si le
 * service est absent, en panne, saturé ou refuse le compte.
 * Tout autre compte : directement l'API, sans detour.
 */
export function creerFetchChat(email?: string | null): typeof fetch {
  if (!passeParAbonnement(email)) return fetch;

  return async (entree, init) => {
    const urlApi = typeof entree === 'string' ? entree : (entree as Request).url;

    const abandon = new AbortController();
    const minuteur = setTimeout(() => abandon.abort(), DELAI_CONNEXION_MS);
    try {
      const reponse = await fetch(`${PIXEL_API_URL}/chat`, {
        ...init,
        signal: abandon.signal,
      });
      clearTimeout(minuteur);

      // Le flux a commence : on ne peut plus replier, et on n'a pas a le faire.
      if (reponse.ok) return reponse;

      // Quota atteint ou session invalide : ce sont des reponses LEGITIMES,
      // identiques a celles de l'API. Replier ici contournerait le quota.
      if (reponse.status === 429 || reponse.status === 401) return reponse;

      // 403 (compte hors liste), 404, 5xx, 503 (saturé) : on passe a l'API.
      console.warn(`[chat] pixel-api a repondu ${reponse.status} — bascule sur l'API`);
      return fetch(urlApi, init);
    } catch (e) {
      clearTimeout(minuteur);
      // Service arrete, Funnel en 502, machine eteinte, delai depasse :
      // exactement le cas d'aout 2026, qui laissait l'utilisateur sans rien.
      console.warn('[chat] pixel-api injoignable — bascule sur l\'API', e);
      return fetch(urlApi, init);
    }
  };
}
